<!-- Logged out this page is a public, footer-linked marketing surface: it gets the same
     app-portal-navbar as home/products. Signed in, it relies on the app shell's own
     header (title resolved from MenuConfigService.publicPages) instead of a page-local
     copy — see AppComponent.hideAppShell$/showPublicNav$. -->
@if (!isAuthenticated) {
<app-portal-navbar></app-portal-navbar>
}

<ion-content #pageContent [scrollEvents]="true">
    <div class="agb-page" [class.agb-page--public]="!isAuthenticated" [class.agb-page--shell]="isAuthenticated">

        <!-- Hero -->
        <div class="agb-hero">
            <div class="agb-hero-icon">
                <ion-icon name="reader-outline"></ion-icon>
            </div>
            <h1>{{ 'mainauth.terms.title' | translate }}</h1>
            <p class="agb-meta">{{ 'mainauth.terms.meta' | translate }}</p>
        </div>

        <!-- Section index + cards. Two columns from 1100px up, stacked below. -->
        <div class="agb-layout">
            <app-legal-toc class="agb-toc" [sections]="tocSections" [content]="pageContent"
                [scrollOffset]="isAuthenticated ? 112 : 80"></app-legal-toc>

        <!-- Sections -->
        <div class="agb-sections">

            <!-- Product terms annexes (ScreenDeck, Chroniq). Always rendered (no more
                 ?product= selector — see docs/KONZEPT-Produkt-Rechtsseiten.md): a reader
                 following any product's link, or a bare /agb link, sees the whole document.
                 Supplements the §§ below rather than replacing them — the base AGB stays
                 the contract. -->
            @for (product of productDocs; track product.key) {
            <div class="agb-card agb-card--product" [id]="product.key">
                <div class="agb-card-header">
                    <div class="agb-num"><ion-icon name="business-outline"></ion-icon></div>
                    <div class="agb-card-title">{{ product.doc.title }}</div>
                </div>
                <p class="agb-meta">{{ product.doc.meta }}</p>
            </div>
            @for (section of product.doc.sections; track section.num) {
            <div class="agb-card" [id]="sectionId(product.key, section.num)">
                <div class="agb-card-header">
                    <div class="agb-num">{{ section.num }}</div>
                    <div class="agb-card-title">{{ section.title }}</div>
                </div>
                <!-- Authored in this repository, never user input; Angular sanitises it regardless.
                     .agb-card-body is what the ::ng-deep block in the stylesheet hooks onto — the
                     component's own scoped rules cannot reach [innerHTML] nodes. -->
                <div class="agb-card-body" [innerHTML]="section.body"></div>
            </div>
            }
            }

            <!-- Marks where the annexes end and the group-wide AGB begin. Only rendered when
                 at least one annex precedes it — otherwise the §§ are the whole document and
                 the label would state the obvious. -->
            @if (productDocs.length > 0) {
            <div class="agb-divider"><span>{{ 'mainauth.terms.title' | translate }}</span></div>
            }

            <!-- Product-specific short notes. Always rendered, for both products. -->
            @for (p of annexProducts; track p) {
            <div class="agb-card agb-card--product" [id]="p">
                <div class="agb-card-header">
                    <div class="agb-num"><ion-icon name="pricetag-outline"></ion-icon></div>
                    <div class="agb-card-title">{{ ('mainauth.terms.product.' + p + '.title') | translate }}</div>
                </div>
                <p>{{ ('mainauth.terms.product.' + p + '.body') | translate }}</p>
            </div>
            }

            <div class="agb-card" id="agb-s1">
                <div class="agb-card-header">
                    <div class="agb-num">§ 1</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s1Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s1Text1' | translate }}</p>
                <p>{{ 'mainauth.terms.s1ProviderPre' | translate }}<strong>Gastro Management Oberbillig</strong>{{ 'mainauth.terms.s1ProviderPost' | translate }}<br>
                    {{ 'mainauth.terms.s1Contact' | translate }} <a href="mailto:management@gastro-management.ch">management&#64;gastro-management.ch</a>
                </p>
                <p><strong>{{ 'mainauth.terms.s1B2bLabel' | translate }}</strong> {{ 'mainauth.terms.s1B2bText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s1GuestLabel' | translate }}</strong> {{ 'mainauth.terms.s1GuestText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s1PrecedenceLabel' | translate }}</strong> {{ 'mainauth.terms.s1PrecedenceText' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s2">
                <div class="agb-card-header">
                    <div class="agb-num">§ 2</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s2Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s2Intro' | translate }}</p>
                <div class="agb-product-list">
                    <div class="agb-product-item">
                        <ion-icon name="grid-outline"></ion-icon>
                        <div>
                            <strong>Gastro Management Portal</strong>
                            <span>{{ 'mainauth.terms.s2Portal' | translate }}</span>
                        </div>
                    </div>
                    <div class="agb-product-item">
                        <ion-icon name="time-outline"></ion-icon>
                        <div>
                            <strong>Chroniq</strong>
                            <span>{{ 'mainauth.terms.s2Chroniq' | translate }}</span>
                        </div>
                    </div>
                    <div class="agb-product-item">
                        <ion-icon name="desktop-outline"></ion-icon>
                        <div>
                            <strong>ScreenDeck</strong>
                            <span>{{ 'mainauth.terms.s2ScreenDeck' | translate }}</span>
                        </div>
                    </div>
                    <div class="agb-product-item">
                        <ion-icon name="calendar-outline"></ion-icon>
                        <div>
                            <strong>TableStack</strong>
                            <span>{{ 'mainauth.terms.s2TableStack' | translate }}</span>
                        </div>
                    </div>
                    <div class="agb-product-item">
                        <ion-icon name="qr-code-outline"></ion-icon>
                        <div>
                            <strong>{{ 'mainauth.terms.s2MenuName' | translate }}</strong>
                            <span>{{ 'mainauth.terms.s2Menu' | translate }}</span>
                        </div>
                    </div>
                </div>
                <p>{{ 'mainauth.terms.s2Changes' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s3">
                <div class="agb-card-header">
                    <div class="agb-num">§ 3</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s3Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s3Text1' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s3TrialLabel' | translate }}</strong> {{ 'mainauth.terms.s3TrialText' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s4">
                <div class="agb-card-header">
                    <div class="agb-num">§ 4</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s4Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s4Text' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s5">
                <div class="agb-card-header">
                    <div class="agb-num">§ 5</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s5Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s5Text' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s5GuestLabel' | translate }}</strong> {{ 'mainauth.terms.s5GuestText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s5AllergenLabel' | translate }}</strong> {{ 'mainauth.terms.s5AllergenText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s5AccountLabel' | translate }}</strong> {{ 'mainauth.terms.s5AccountText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s5EuLabel' | translate }}</strong> {{ 'mainauth.terms.s5EuText' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s6">
                <div class="agb-card-header">
                    <div class="agb-num">§ 6</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s6Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s6Intro' | translate }}</p>
                <ul>
                    <li>{{ 'mainauth.terms.s6Illegal' | translate }}</li>
                    <li>{{ 'mainauth.terms.s6Security' | translate }}</li>
                    <li>{{ 'mainauth.terms.s6Load' | translate }}</li>
                    <li>{{ 'mainauth.terms.s6Resell' | translate }}</li>
                    <li>{{ 'mainauth.terms.s6Sensitive' | translate }}</li>
                </ul>
                <p>{{ 'mainauth.terms.s6Consequence' | translate }}</p>
            </div>

            <!-- EU AI Act (Reg. (EU) 2024/1689), generally applicable from 02.08.2026 -->
            <div class="agb-card" id="agb-s7">
                <div class="agb-card-header">
                    <div class="agb-num">§ 7</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s7Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s7Intro' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s7RolesLabel' | translate }}</strong> {{ 'mainauth.terms.s7RolesText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s7NoWarrantyLabel' | translate }}</strong> {{ 'mainauth.terms.s7NoWarrantyText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s7LiteracyLabel' | translate }}</strong> {{ 'mainauth.terms.s7LiteracyText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s7ProhibitedLabel' | translate }}</strong> {{ 'mainauth.terms.s7ProhibitedText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s7OptOutLabel' | translate }}</strong> {{ 'mainauth.terms.s7OptOutText' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s8">
                <div class="agb-card-header">
                    <div class="agb-num">§ 8</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s8Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s8Text' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s8TaxLabel' | translate }}</strong> {{ 'mainauth.terms.s8TaxText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s8FxLabel' | translate }}</strong> {{ 'mainauth.terms.s8FxText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s8ThirdPartyLabel' | translate }}</strong> {{ 'mainauth.terms.s8ThirdPartyText' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s9">
                <div class="agb-card-header">
                    <div class="agb-num">§ 9</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s9Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s9Text' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s9CauseLabel' | translate }}</strong> {{ 'mainauth.terms.s9CauseText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s9SuspendLabel' | translate }}</strong> {{ 'mainauth.terms.s9SuspendText' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s10">
                <div class="agb-card-header">
                    <div class="agb-num">§ 10</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s10Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s10Intro' | translate }}</p>
                <ul>
                    <li><strong>Chroniq:</strong> 99.8 %</li>
                    <li><strong>ScreenDeck:</strong> 99.9 %</li>
                    <li><strong>Gastro Management Portal:</strong> 99.5 %</li>
                    <li><strong>TableStack:</strong> 99.5 %</li>
                </ul>
                <p>{{ 'mainauth.terms.s10Text' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s10SupportLabel' | translate }}</strong> {{ 'mainauth.terms.s10SupportText' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s11">
                <div class="agb-card-header">
                    <div class="agb-num">§ 11</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s11Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s11Text1' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s11DpaLabel' | translate }}</strong> {{ 'mainauth.terms.s11DpaText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s11SubLabel' | translate }}</strong> {{ 'mainauth.terms.s11SubText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s11EuLabel' | translate }}</strong> {{ 'mainauth.terms.s11EuText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s11UsLabel' | translate }}</strong> {{ 'mainauth.terms.s11UsText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s11BreachLabel' | translate }}</strong> {{ 'mainauth.terms.s11BreachText' | translate }}</p>
                <a routerLink="/privacy" class="agb-link">
                    <ion-icon name="arrow-forward-outline"></ion-icon>
                    {{ 'mainauth.terms.s11PrivacyLink' | translate }}
                </a>
            </div>

            <!-- EU Data Act (Reg. (EU) 2023/2854), applicable since 12.09.2025 -->
            <div class="agb-card" id="agb-s12">
                <div class="agb-card-header">
                    <div class="agb-num">§ 12</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s12Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s12Text1' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s12ExportLabel' | translate }}</strong> {{ 'mainauth.terms.s12ExportText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s12SwitchLabel' | translate }}</strong> {{ 'mainauth.terms.s12SwitchText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s12DeletionLabel' | translate }}</strong> {{ 'mainauth.terms.s12DeletionText' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s13">
                <div class="agb-card-header">
                    <div class="agb-num">§ 13</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s13Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s13Text1' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s13RemoteLabel' | translate }}</strong> {{ 'mainauth.terms.s13RemoteText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s13DataActLabel' | translate }}</strong> {{ 'mainauth.terms.s13DataActText' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s14">
                <div class="agb-card-header">
                    <div class="agb-num">§ 14</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s14Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s14Text1' | translate }}</p>
                <p>{{ 'mainauth.terms.s14Text2' | translate }}</p>
                <p class="agb-caps">{{ 'mainauth.terms.s14Disclaimer' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s15">
                <div class="agb-card-header">
                    <div class="agb-num">§ 15</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s15Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s15Text1' | translate }}</p>
                <p>{{ 'mainauth.terms.s15Text2' | translate }}</p>
                <p>{{ 'mainauth.terms.s15Text3' | translate }}</p>
                <p class="agb-caps">{{ 'mainauth.terms.s15Cap' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s16">
                <div class="agb-card-header">
                    <div class="agb-num">§ 16</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s16Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s16Text' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s17">
                <div class="agb-card-header">
                    <div class="agb-num">§ 17</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s17Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s17Text1' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s17OssLabel' | translate }}</strong> {{ 'mainauth.terms.s17OssText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s17FeedbackLabel' | translate }}</strong> {{ 'mainauth.terms.s17FeedbackText' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s18">
                <div class="agb-card-header">
                    <div class="agb-num">§ 18</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s18Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s18Text' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s19">
                <div class="agb-card-header">
                    <div class="agb-num">§ 19</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s19Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s19Text' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s20">
                <div class="agb-card-header">
                    <div class="agb-num">§ 20</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s20Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s20Text' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s21">
                <div class="agb-card-header">
                    <div class="agb-num">§ 21</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s21Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s21Text' | translate }}</p>
            </div>

            <div class="agb-card" id="agb-s22">
                <div class="agb-card-header">
                    <div class="agb-num">§ 22</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s22Title' | translate }}</div>
                </div>
                <ul>
                    <li><strong>{{ 'mainauth.terms.s22FormLabel' | translate }}</strong> {{ 'mainauth.terms.s22FormText' | translate }}</li>
                    <li><strong>{{ 'mainauth.terms.s22AssignLabel' | translate }}</strong> {{ 'mainauth.terms.s22AssignText' | translate }}</li>
                    <li><strong>{{ 'mainauth.terms.s22SeverLabel' | translate }}</strong> {{ 'mainauth.terms.s22SeverText' | translate }}</li>
                    <li><strong>{{ 'mainauth.terms.s22EntireLabel' | translate }}</strong> {{ 'mainauth.terms.s22EntireText' | translate }}</li>
                    <li><strong>{{ 'mainauth.terms.s22LanguageLabel' | translate }}</strong> {{ 'mainauth.terms.s22LanguageText' | translate }}</li>
                    <li><strong>{{ 'mainauth.terms.s22ReferenceLabel' | translate }}</strong> {{ 'mainauth.terms.s22ReferenceText' | translate }}</li>
                </ul>
            </div>

            <div class="agb-card" id="agb-s23">
                <div class="agb-card-header">
                    <div class="agb-num">§ 23</div>
                    <div class="agb-card-title">{{ 'mainauth.terms.s23Title' | translate }}</div>
                </div>
                <p>{{ 'mainauth.terms.s23Text1' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s23EuLabel' | translate }}</strong> {{ 'mainauth.terms.s23EuText' | translate }}</p>
                <p><strong>{{ 'mainauth.terms.s23UsLabel' | translate }}</strong> {{ 'mainauth.terms.s23UsText' | translate }}</p>
            </div>

            </div>
        </div>

        <!-- Footer note -->
        <div class="agb-footer">
            <ion-icon name="document-text-outline"></ion-icon>
            <span>{{ 'mainauth.terms.footer' | translate }}</span>
        </div>

    </div>

    <!-- Outside .agb-page so it spans the full width like on the other marketing pages. -->
    <app-portal-footer></app-portal-footer>
</ion-content>
